import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { ROUTES } from '@/constants/routes';

import { useAuth } from './AuthContext';
import type { UserRole } from './schemas';

type Props = {
  children: ReactNode;
  /** Restrict to one or more roles. If omitted, any authenticated user. */
  allowedRoles?: UserRole[];
};

/**
 * Wrap protected routes. Waits for the auth check to finish before
 * redirecting — avoids flashing the login page for valid sessions.
 */
export function ProtectedRoute({ children, allowedRoles }: Props) {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center p-6">
        <div className="max-w-md rounded-2xl border border-yellow-200 bg-yellow-50 p-6 text-center">
          <h2 className="text-lg font-semibold text-yellow-900">Access denied</h2>
          <p className="mt-2 text-sm text-yellow-800">
            You don&apos;t have permission to view this page.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
